import { createApi } from '@reduxjs/toolkit/dist/query/react';

import {
  getBaseQuery,
  getListProvidesTags,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import * as endpoints from '../endpoints';
import { REDUX_CACHE, PROGRAMS, HTTP_METHOD } from '../constants';
import { IProgram } from '../../../types/interfaces/program';
import { IProgramItem } from '../../../types/interfaces/program-item';
import { IExercise } from '../../../types/interfaces/exercise';

export const programsApi = createApi({
  reducerPath: 'programsApi',
  tagTypes: [
    PROGRAMS.AVAILABLE,
    PROGRAMS.PROGRAMS,
    PROGRAMS.PROGRAM_ITEMS,
    PROGRAMS.EXERCISES,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getUserProgramsApi: builder.query<
      IProgram[],
      { includeItems?: boolean; includeAvailableToUsers?: boolean }
    >({
      keepUnusedDataFor: 3600,
      query({ includeItems, includeAvailableToUsers }) {
        const queryString: string[] = [];

        if (includeItems) {
          queryString.push('includeItems=true');
        }

        if (includeAvailableToUsers) {
          queryString.push('includeAvailableToUsers=true');
        }

        return {
          url: `${endpoints.Program.ROOT}${queryString.length ? '?' + queryString.join('&') : ''}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => getListProvidesTags(result, PROGRAMS.PROGRAMS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getAvailableUserProgramsApi: builder.query<
      IProgram[],
      { includeItems?: boolean } | void
    >({
      keepUnusedDataFor: 3600,
      query(includeItems) {
        return {
          url: `${endpoints.Program.ROOT}${endpoints.Program.AVAILABLE}${includeItems ? '?includeItems=true' : ''}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => getListProvidesTags(result, PROGRAMS.AVAILABLE),
      ...TransformedErrorResponseWithNotification(),
    }),
    getProgramByIdApi: builder.query<IProgram, string>({
      keepUnusedDataFor: 3600,
      query(id) {
        return {
          url: `${endpoints.Program.ROOT}/${id}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => [{ type: PROGRAMS.PROGRAMS, id }],
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutProgramApi: builder.mutation<void, { id: string; body: IProgram }>({
      query({ id, body }) {
        return {
          url: endpoints.Program.ROOT + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAMS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: PROGRAMS.PROGRAMS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Program'),
    }),
    deleteProgramsApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.Program.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAMS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Program'),
    }),
    getProgramItemsApi: builder.query<IProgramItem[], { programId: string }>({
      keepUnusedDataFor: 3600,
      query({ programId }) {
        return {
          url: `${endpoints.Program.ROOT}/${programId}${endpoints.Program.ITEM}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, PROGRAMS.PROGRAM_ITEMS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getProgramItemByIdApi: builder.query<IProgramItem, string>({
      keepUnusedDataFor: 3600,
      query(itemId) {
        return {
          url: `${endpoints.Program.ROOT}${endpoints.Program.ITEM}/${itemId}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => {
        return [{ type: PROGRAMS.PROGRAM_ITEMS, id }];
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getProgramItemExercisesApi: builder.query<IExercise[], string>({
      keepUnusedDataFor: 3600,
      query(itemId) {
        return {
          url: `${endpoints.Program.ROOT}${endpoints.Program.ITEM}/${itemId}${endpoints.Program.EXERCISES}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => {
        return [{ type: PROGRAMS.EXERCISES, id }];
      },
      ...TransformedErrorResponseWithNotification(),
    }),

    postPutProgramItemApi: builder.mutation<
      void,
      { programId: string; itemId: string; body: IProgramItem }
    >({
      query({ programId, itemId, body }) {
        return {
          url:
            `${endpoints.Program.ROOT}/${programId}${endpoints.Program.ITEM}` +
            (itemId ? `/${itemId}` : ''),
          method: itemId ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAM_ITEMS,
              id: params.itemId,
            },
            {
              type: PROGRAMS.PROGRAMS,
              id: params.programId,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Program item'),
    }),
    deleteProgramItemsApi: builder.mutation<
      void,
      { programId: string; itemId: string }
    >({
      query({ programId, itemId }) {
        return {
          url: `${endpoints.Program.ROOT}/${programId}${endpoints.Program.ITEM}/${itemId}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAM_ITEMS,
              id: params.itemId,
            },
            {
              type: PROGRAMS.PROGRAMS,
              id: params.programId,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Program item'),
    }),
    copyProgramItemApi: builder.mutation<
      void,
      { programId: string; itemId: string }
    >({
      query({ programId, itemId }) {
        return {
          url: `${endpoints.Program.ROOT}${endpoints.Program.ITEM}/${itemId}${endpoints.Program.COPY}`,
          method: HTTP_METHOD.POST,
          body: {
            programId,
          },
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAMS,
              id: params.programId,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Program item'),
    }),
    shareProgramApi: builder.mutation<
      void,
      { programId: string; userId: string }
    >({
      query({ programId, userId }) {
        return {
          url: `${endpoints.Program.ROOT}/${programId}${endpoints.Program.USER}/${userId}`,
          method: HTTP_METHOD.POST,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAMS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: PROGRAMS.AVAILABLE,
              id: params.programId,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Program'),
    }),
    unshareProgramApi: builder.mutation<
      void,
      { programId: string; userId: string }
    >({
      query({ programId, userId }) {
        return {
          url: `${endpoints.Program.ROOT}/${programId}${endpoints.Program.USER}/${userId}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAMS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: PROGRAMS.AVAILABLE,
              id: params.programId,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Program'),
    }),
  }),
});

export const {
  useGetAvailableUserProgramsApiQuery: useGetAvailableUserPrograms,
  useGetUserProgramsApiQuery: useGetUserPrograms,
  useGetProgramByIdApiQuery: useGetProgramById,
  useLazyGetProgramByIdApiQuery: useLazyGetProgramById,
  usePostPutProgramApiMutation: usePostPutProgram,
  useDeleteProgramsApiMutation: useDeletePrograms,
  useGetProgramItemsApiQuery: useGetProgramItems,
  useGetProgramItemByIdApiQuery: useGetProgramItemById,
  useLazyGetProgramItemByIdApiQuery: useLazyGetProgramItemById,
  usePostPutProgramItemApiMutation: usePostPutProgramItem,
  useCopyProgramItemApiMutation: useCopyProgramItemApi,
  useDeleteProgramItemsApiMutation: useDeleteProgramItems,
  useShareProgramApiMutation: useShareProgram,
  useUnshareProgramApiMutation: useUnshareProgram,
  useLazyGetProgramItemExercisesApiQuery: useLazyGetProgramItemExercises,
} = programsApi;
